import { useEffect, useState, useMemo, useCallback } from 'react';
import { KpiAdminService } from '../../api/kpi/KpiAdminService';
import {
  FaShieldAlt, FaChevronLeft, FaChevronRight,
  FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
  FaSyncAlt, FaSearch, FaRedo, FaDatabase, FaPaperPlane,
  FaTimes, FaArrowLeft
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './KpiAdmin.css';

const STATUS_BADGE = {
  SUCCESS:  { color: '#52c41a', label: 'SUCCESS' },
  VERIFIED: { color: '#1890ff', label: 'VERIFIED' },
  FAILED:   { color: '#ff4d4f', label: 'FAILED' },
  PENDING:  { color: '#faad14', label: 'PENDING' },
};

// 부분-stale(SENSOR_STALE 의 하위 라벨) 표시. partial 이 아닐 때는 아무것도 렌더하지 않는다.
// detail 이 비어 있으면 "(일부 라인)" fallback 사용.
const PartialStaleLabel = ({ partial, detail }) => {
  if (!partial) return null;
  return (
    <div className="due-partial-stale" title="회사 센서는 정상이나 일부 생산라인 센서가 끊김">
      {detail || '(일부 라인)'}
    </div>
  );
};

const KpiAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [dueStatus, setDueStatus] = useState(null);
  const [dueFilter, setDueFilter] = useState('attention'); // 'all'|'attention'|'PENDING'|'DUE'|'MISSED'
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [toast, setToast] = useState('');

  // 탭
  const [tab, setTab] = useState('push'); // 'push' | 'db'

  // 날짜 선택
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);

  // 검색
  const [searchText, setSearchText] = useState('');

  // 업체 상세 모달
  const [detailModal, setDetailModal] = useState(null);
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const monthLabel = `${selectedYear}년 ${selectedMonth}월`;

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;
  }, [selectedYear, selectedMonth]);

  const goToPrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedYear(y => y - 1);
      setSelectedMonth(12);
    } else {
      setSelectedMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    const now = new Date();
    const nextM = selectedMonth === 12 ? 1 : selectedMonth + 1;
    const nextY = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
    if (nextY > now.getFullYear() || (nextY === now.getFullYear() && nextM > now.getMonth() + 1)) return;
    setSelectedYear(nextY);
    setSelectedMonth(nextM);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setVerifyResult(null);
    try {
      const [reportData, coverageData, dueStatusData] = await Promise.all([
        KpiAdminService.report(selectedYear, selectedMonth),
        KpiAdminService.coverage(selectedYear, selectedMonth),
        KpiAdminService.dueStatus(),
      ]);
      setReport(reportData);
      setCoverage(coverageData);
      setDueStatus(dueStatusData);
    } catch (e) {
      console.error('KPI Admin fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 검증
  const handleVerify = async () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const pushDate = yesterday.toISOString().slice(0, 10);

    setVerifying(true);
    try {
      const result = await KpiAdminService.verify(pushDate);
      setVerifyResult(result);
      showToast(`검증 완료: ${result.verified || 0}건 검증, ${result.failed || 0}건 실패`);
      await fetchData();
    } catch (e) {
      setVerifyResult({ error: e.message });
    } finally {
      setVerifying(false);
    }
  };

  // 전체 재시도
  const handleRetryAll = async () => {
    if (!window.confirm('실패 건을 전체 재시도하시겠습니까?')) return;
    setRetrying(true);
    try {
      const result = await KpiAdminService.retryAll();
      showToast(result.message || `${result.count}건 재시도 완료`);
      await fetchData();
    } catch (e) {
      showToast('재시도 실패: ' + e.message);
    } finally {
      setRetrying(false);
    }
  };

  // 업체별 재시도
  const handleRetryCompany = async (companySeq, companyName) => {
    setRetrying(true);
    try {
      const result = await KpiAdminService.retryCompany(companySeq);
      showToast(`${companyName}: ${result.message}`);
      await fetchData();
    } catch (e) {
      showToast(`${companyName} 재시도 실패`);
    } finally {
      setRetrying(false);
    }
  };

  // 수동 Push (어제 날짜)
  const handlePushBatch = async () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const pushDate = yesterday.toISOString().slice(0, 10);

    if (!window.confirm(`${pushDate} 전체 업체 Push를 실행하시겠습니까?`)) return;
    setPushing(true);
    try {
      const result = await KpiAdminService.pushBatch(pushDate);
      showToast(`Push 완료: 성공 ${result.success}, 실패 ${result.failed}`);
      await fetchData();
    } catch (e) {
      showToast('Push 실패: ' + e.message);
    } finally {
      setPushing(false);
    }
  };

  // 업체 상세 모달
  const openDetail = async (companySeq, companyName) => {
    setDetailModal({ companySeq, companyName });
    setDetailLoading(true);
    try {
      const data = await KpiAdminService.companyStatus(companySeq, 50);
      setDetailLogs(data.logs || []);
    } catch (e) {
      setDetailLogs([]);
    } finally {
      setDetailLoading(false);
    }
  };

  // 요약 통계
  const summaryStats = useMemo(() => {
    if (!report?.summary) return { total: 0, success: 0, verified: 0, failed: 0 };
    const stats = { total: 0, success: 0, verified: 0, failed: 0 };
    report.summary.forEach(s => {
      const cnt = parseInt(s.cnt, 10);
      stats.total += cnt;
      if (s.status === 'SUCCESS') stats.success += cnt;
      if (s.status === 'VERIFIED') stats.verified += cnt;
      if (s.status === 'FAILED') stats.failed += cnt;
    });
    return stats;
  }, [report]);

  // 실패 업체 필터
  const filteredFailures = useMemo(() => {
    if (!report?.failures) return [];
    if (!searchText) return report.failures;
    const q = searchText.toLowerCase();
    return report.failures.filter(f =>
      (f.company_name || '').toLowerCase().includes(q) ||
      String(f.company_seq).includes(q)
    );
  }, [report, searchText]);

  // DB 수집 업체 필터
  const filteredCoverage = useMemo(() => {
    if (!coverage?.companies) return [];
    if (!searchText) return coverage.companies;
    const q = searchText.toLowerCase();
    return coverage.companies.filter(c =>
      (c.company_name || '').toLowerCase().includes(q)
    );
  }, [coverage, searchText]);

  // dueStatus (send_period 기반 누락 + 사유) 필터
  const dueCompanies = useMemo(() => {
    const list = dueStatus?.companies || [];
    let filtered = list;
    if (dueFilter === 'attention') {
      filtered = list.filter(c => c.companyDueStatus === 'MISSED' || c.companyDueStatus === 'DUE');
    } else if (dueFilter !== 'all') {
      filtered = list.filter(c => c.companyDueStatus === dueFilter);
    }
    if (searchText) {
      filtered = filtered.filter(c => (c.companyName || '').includes(searchText));
    }
    return filtered;
  }, [dueStatus, dueFilter, searchText]);

  // 상세 모달 대상 회사의 dueStatus 지표 배열
  const detailDue = useMemo(() => {
    if (!detailModal || !dueStatus) return null;
    return (dueStatus.companies || []).find(c => c.companySeq === detailModal.companySeq) || null;
  }, [detailModal, dueStatus]);

  return (
    <div className="kpi-admin">
      <div className="kpi-admin-header">
        <div className="kpi-admin-title">
          <h3>
            <FaArrowLeft style={{ cursor: 'pointer', marginRight: 12 }} onClick={() => navigate('/admin')} />
            <FaShieldAlt style={{ marginRight: 8 }} />KPI Push 관리
          </h3>
        </div>
        <div className="kpi-admin-controls">
          <button className="kpi-admin-btn push-btn" onClick={handlePushBatch} disabled={pushing}>
            <FaPaperPlane /> {pushing ? 'Push 중...' : '어제 건 Push'}
          </button>
          <button className="kpi-admin-btn verify-btn" onClick={handleVerify} disabled={verifying}>
            <FaSyncAlt className={verifying ? 'spin' : ''} />
            {verifying ? '검증 중...' : '어제 건 검증'}
          </button>
          <button className="kpi-admin-btn retry-btn" onClick={handleRetryAll} disabled={retrying}>
            <FaRedo className={retrying ? 'spin' : ''} />
            {retrying ? '재시도 중...' : '실패 건 재시도'}
          </button>
          <div className="month-picker">
            <button className="month-picker-btn" onClick={goToPrevMonth}><FaChevronLeft /></button>
            <span className="month-picker-label">{monthLabel}</span>
            <button className="month-picker-btn" onClick={goToNextMonth} disabled={isCurrentMonth}>
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* 탭 */}
      <div className="kpi-admin-tabs">
        <button className={`tab-btn ${tab === 'push' ? 'active' : ''}`} onClick={() => setTab('push')}>
          <FaPaperPlane /> 전송 현황
        </button>
        <button className={`tab-btn ${tab === 'db' ? 'active' : ''}`} onClick={() => setTab('db')}>
          <FaDatabase /> DB 수집 현황
        </button>
      </div>

      {/* 검증 결과 알림 */}
      {verifyResult && (
        <div className={`kpi-admin-alert ${verifyResult.error ? 'alert-error' : 'alert-success'}`}>
          {verifyResult.error
            ? `검증 오류: ${verifyResult.error}`
            : `검증 완료 - ${verifyResult.push_date}: ${verifyResult.verified}건 검증, ${verifyResult.failed}건 실패`
          }
        </div>
      )}

      {loading ? (
        <div className="kpi-admin-loading">데이터 로딩 중...</div>
      ) : tab === 'push' ? (
        /* ========== 전송 현황 탭 ========== */
        <>
          {/* 요약 카드 */}
          <div className="kpi-admin-section">
            <h4 className="section-title">전송 현황 요약 ({monthLabel})</h4>
            <div className="stat-cards">
              <div className="stat-card">
                <span className="stat-number">{summaryStats.total}</span>
                <span className="stat-label">전체</span>
              </div>
              <div className="stat-card stat-verified">
                <FaCheckCircle />
                <span className="stat-number">{summaryStats.verified}</span>
                <span className="stat-label">검증 완료</span>
              </div>
              <div className="stat-card stat-success">
                <FaCheckCircle />
                <span className="stat-number">{summaryStats.success}</span>
                <span className="stat-label">전송 성공</span>
              </div>
              <div className="stat-card stat-failed">
                <FaTimesCircle />
                <span className="stat-number">{summaryStats.failed}</span>
                <span className="stat-label">실패</span>
              </div>
            </div>
          </div>

          {/* 실패 목록 */}
          <div className="kpi-admin-section">
            <div className="section-header">
              <h4 className="section-title">
                <FaExclamationTriangle style={{ color: '#ff4d4f' }} />
                실패 목록 ({report?.failure_count || 0}건)
              </h4>
              {report?.failures?.length > 0 && (
                <div className="search-box">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="업체명 검색..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                  />
                </div>
              )}
            </div>
            {filteredFailures.length > 0 ? (
              <table className="kpi-admin-table">
                <thead>
                  <tr>
                    <th>업체명</th>
                    <th>날짜</th>
                    <th>레벨</th>
                    <th>지표</th>
                    <th>에러</th>
                    <th>재시도</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFailures.map((f, i) => (
                    <tr key={i}>
                      <td
                        className="clickable-name"
                        onClick={() => openDetail(f.company_seq, f.company_name)}
                      >
                        {f.company_name}
                      </td>
                      <td>{f.push_date}</td>
                      <td>Lv{f.level}</td>
                      <td className="code-cell">{f.kpi_fld_cd ? `${f.kpi_fld_cd}/${f.kpi_dtl_cd}` : '-'}</td>
                      <td className="error-cell">{f.error_message || '-'}</td>
                      <td>{f.retry_count}/3</td>
                      <td>
                        {f.retry_count < 3 && (
                          <button
                            className="inline-btn retry-inline"
                            onClick={() => handleRetryCompany(f.company_seq, f.company_name)}
                            disabled={retrying}
                          >
                            <FaRedo /> 재시도
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">실패 건이 없습니다</div>
            )}
          </div>

          {/* dueStatus 표 (send_period 기반 누락 + 사유) */}
          {dueStatus && (
            <div className="due-section">
              <div className="due-header-row">
                <h3>전송 예정/누락 현황 {dueStatus.evaluatedAt && <span className="due-eval-date">(기준일 {dueStatus.evaluatedAt})</span>}</h3>
                <div className="due-toggle">
                  {[
                    ['attention', '주의 (DUE+MISSED)'],
                    ['MISSED', 'MISSED'],
                    ['DUE', 'DUE'],
                    ['PENDING', 'PENDING'],
                    ['all', '전체'],
                  ].map(([val, label]) => (
                    <button
                      key={val}
                      className={`due-toggle-btn ${dueFilter === val ? 'active' : ''}`}
                      onClick={() => setDueFilter(val)}
                    >{label}</button>
                  ))}
                </div>
              </div>
              <table className="due-table">
                <thead>
                  <tr>
                    <th>업체명</th><th>상태</th><th>사유</th>
                    <th>최종 전송일</th><th>다음 예정일</th>
                  </tr>
                </thead>
                <tbody>
                  {dueCompanies.map(c => {
                    const firstInd = c.indicators?.[0] || {};
                    return (
                      <tr key={c.companySeq}
                          className="due-row"
                          onClick={() => openDetail(c.companySeq, c.companyName)}>
                        <td>{c.companyName}</td>
                        <td><span className={`due-badge due-${c.companyDueStatus?.toLowerCase()}`}>{c.companyDueStatus}</span></td>
                        <td>
                          {c.companyNotSentReasonText || '-'}
                          {c.companyNotSentReason === 'MANUAL_PAUSE' && c.pauseReason && (
                            <div className="due-pause-reason">{c.pauseReason}</div>
                          )}
                          {c.companyNotSentReason === 'SENSOR_STALE' && (
                            <PartialStaleLabel
                              partial={c.companyPartialStale}
                              detail={c.companyNotSentReasonDetail}
                            />
                          )}
                        </td>
                        <td>{firstInd.lastPushedAt || '-'}</td>
                        <td>{firstInd.nextDueDate || '-'}</td>
                      </tr>
                    );
                  })}
                  {dueCompanies.length === 0 && (
                    <tr><td colSpan={5} className="due-empty">해당 상태의 업체가 없습니다.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        /* ========== DB 수집 현황 탭 ========== */
        <>
          {coverage && (
            <>
              {/* 수집 요약 */}
              <div className="kpi-admin-section">
                <h4 className="section-title">DB 수집 현황 ({monthLabel})</h4>
                <div className="stat-cards">
                  <div className="stat-card">
                    <span className="stat-number">{coverage.business_days}</span>
                    <span className="stat-label">영업일 수</span>
                  </div>
                  <div className="stat-card stat-success">
                    <FaDatabase />
                    <span className="stat-number">{coverage.companies?.length || 0}</span>
                    <span className="stat-label">데이터 보유 업체</span>
                  </div>
                  <div className="stat-card stat-failed">
                    <FaExclamationTriangle />
                    <span className="stat-number">{coverage.no_data_count || 0}</span>
                    <span className="stat-label">데이터 없는 업체</span>
                  </div>
                </div>
              </div>

              {/* 업체별 수집 현황 */}
              <div className="kpi-admin-section">
                <div className="section-header">
                  <h4 className="section-title">
                    업체별 수집 현황 ({filteredCoverage.length}개)
                  </h4>
                  <div className="search-box">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="업체명 검색..."
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                    />
                  </div>
                </div>
                {filteredCoverage.length > 0 ? (
                  <table className="kpi-admin-table">
                    <thead>
                      <tr>
                        <th>업체명</th>
                        <th>수집 일수</th>
                        <th>커버리지</th>
                        <th>첫 데이터</th>
                        <th>마지막 데이터</th>
                        <th>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCoverage.map((c, i) => {
                        const pct = coverage.business_days > 0
                          ? Math.round((c.data_days / coverage.business_days) * 100) : 0;
                        const status = pct >= 90 ? 'good' : pct >= 50 ? 'warn' : 'bad';
                        return (
                          <tr key={i} onClick={() => openDetail(c.company_seq, c.company_name)}>
                            <td className="clickable-name">{c.company_name}</td>
                            <td>{c.data_days} / {coverage.business_days}</td>
                            <td>
                              <div className="coverage-bar-container">
                                <div className={`coverage-bar coverage-${status}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                <span className="coverage-pct">{pct}%</span>
                              </div>
                            </td>
                            <td>{c.first_date || '-'}</td>
                            <td>{c.last_date || '-'}</td>
                            <td>
                              <span className={`coverage-status coverage-status-${status}`}>
                                {status === 'good' ? '정상' : status === 'warn' ? '부분' : '부족'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data">데이터가 없습니다</div>
                )}
              </div>

              {/* 데이터 없는 업체 */}
              {coverage.no_data_companies?.length > 0 && (
                <div className="kpi-admin-section">
                  <h4 className="section-title">
                    <FaExclamationTriangle style={{ color: '#ff4d4f' }} />
                    데이터 없는 업체 ({coverage.no_data_count}개)
                  </h4>
                  <div className="no-push-list">
                    {coverage.no_data_companies.map(c => (
                      <span className="no-push-tag no-data-tag" key={c.company_seq}>
                        {c.company_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* 업체 상세 모달 */}
      {detailModal && (
        <div className="kpi-modal-overlay" onClick={() => setDetailModal(null)}>
          <div className="kpi-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="kpi-modal-header">
              <span>{detailModal.companyName} - Push 이력</span>
              <button className="kpi-modal-close" onClick={() => setDetailModal(null)}><FaTimes /></button>
            </div>
            <div className="kpi-detail-body">
              {detailDue?.indicators?.length > 0 && (
                <div className="detail-due-block">
                  <h4>지표별 전송 현황</h4>
                  <table className="detail-due-table">
                    <thead>
                      <tr><th>지표</th><th>주기</th><th>상태</th><th>사유</th><th>최종</th><th>예정</th></tr>
                    </thead>
                    <tbody>
                      {detailDue.indicators.map((ind, i) => (
                        <tr key={i}>
                          <td>{ind.indicatorName || `${ind.kpiFldCd}/${ind.kpiDtlCd}`}</td>
                          <td>{ind.sendPeriod}</td>
                          <td><span className={`due-badge due-${ind.dueStatus?.toLowerCase()}`}>{ind.dueStatus}</span></td>
                          <td>
                            {ind.notSentReasonText || '-'}
                            {ind.notSentReason === 'SENSOR_STALE' && (
                              <PartialStaleLabel
                                partial={ind.extra?.partialStale}
                                detail={ind.extra?.reasonDetail}
                              />
                            )}
                          </td>
                          <td>{ind.lastPushedAt || '-'}</td>
                          <td>{ind.nextDueDate || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {detailLoading ? (
                <div className="kpi-admin-loading">로딩 중...</div>
              ) : detailLogs.length === 0 ? (
                <div className="no-data">이력이 없습니다</div>
              ) : (
                <table className="kpi-admin-table">
                  <thead>
                    <tr>
                      <th>날짜</th>
                      <th>레벨</th>
                      <th>지표</th>
                      <th>상태</th>
                      <th>에러</th>
                      <th>재시도</th>
                      <th>전송 시각</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailLogs.map((log, i) => (
                      <tr key={i}>
                        <td>{log.push_date}</td>
                        <td>Lv{log.level}</td>
                        <td className="code-cell">
                          {log.kpi_fld_cd ? `${log.kpi_fld_cd}/${log.kpi_dtl_cd}` : '-'}
                        </td>
                        <td>
                          <span
                            className="status-badge"
                            style={{ background: STATUS_BADGE[log.status]?.color || '#666' }}
                          >
                            {STATUS_BADGE[log.status]?.label || log.status}
                          </span>
                        </td>
                        <td className="error-cell">{log.error_message || '-'}</td>
                        <td>{log.retry_count}/3</td>
                        <td className="time-cell">{log.pushed_at ? log.pushed_at.slice(0, 19) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {toast && <div className="kpi-admin-toast">{toast}</div>}
    </div>
  );
};

export default KpiAdmin;
